import { Component, Input, OnInit, Optional } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NbDialogRef, NbDialogService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { SubscribedComponent } from '../../subscribed.component';
import { OpportunityService } from '../../shared/opportunity.service';
import { SubmissionService } from '../../shared/submission.service';
import { UsersService } from '../../shared/users.service';
import { UserRef } from '../../interfaces/user';
import { AuthService } from '../../shared/auth.service';
import { Globals } from '../../shared/Globals';
import { NotifierService } from '../../shared/notifier.service';
import { OrganizationCrmService, Contact, DealContact } from '../../shared/organization-crm.service';
import { ClientService } from '../../shared/client.service';
import { DirectoryService } from '../../shared/directory.service';
import { ClientRef } from '../../interfaces/client';
import { CrmActivity, Opportunity, OpportunityStatus } from '../../interfaces/opportunity';
import { ProjectRole } from '../../interfaces/projectRole';
import { Skill } from '../../interfaces/skill';
import { Submission } from '../../interfaces/submission';
import { AddSubmissionDialogComponent } from './add-submission-dialog/add-submission-dialog.component';
import { SubmissionDetailComponent } from '../submission-detail/submission-detail.component';
import { PopupDialogComponent } from '../../dialogs/popup-dialog/popup-dialog.component';
import { Location } from '@angular/common';
import { opportunityDescription } from '../opportunity-description';
import { QuoteService } from '../../shared/quote.service';
import { QuoteListItem } from '../../interfaces/quote';

/** Statuses a user may explicitly pick; Won is derived and excluded. */
export const OPPORTUNITY_SELECTABLE_STATUSES: OpportunityStatus[] = ['open', 'on_hold', 'lost', 'archived'];

@Component({
  selector: 'bst-opportunity-detail',
  templateUrl: './opportunity-detail.component.html',
  styleUrls: ['./opportunity-detail.component.scss'],
  standalone: false
})
export class OpportunityDetailComponent extends SubscribedComponent implements OnInit {

  @Input() id: number;
  @Input() embedded = false;

  opportunity: Opportunity | null = null;
  submissions: Submission[] = [];
  activities: CrmActivity[] = [];
  contacts: DealContact[] = [];
  quotes: QuoteListItem[] = [];
  creatingQuote = false;

  /** Description starts collapsed; a scraped one runs to hundreds of lines. */
  descExpanded = false;
  readonly descriptionOf = opportunityDescription;

  /** Working copy for the status dropdown — updated without touching the loaded model. */
  selectedStatus: OpportunityStatus = 'open';

  /** Working copy for the primary-contact picker. */
  selectedContactId: number | null = null;

  /** Owner (verantwoordelijke) reassignment: tenant staffers/admins. */
  ownerCandidates: UserRef[] = [];
  selectedOwnerId: number | null = null;
  savingOwner = false;

  /** End-client reassignment: any already-known Client of this tenant. */
  orgCandidates: ClientRef[] = [];
  selectedOrgId: number | null = null;
  savingOrg = false;

  /** Role reassignment: tenant's ProjectRole directory, often empty — it ships unseeded per tenant. */
  roleOptions: ProjectRole[] = [];
  selectedRoleId: number | null = null;
  savingRole = false;

  /** Structured catalogue skills for this deal, editable via the list-adder picker. */
  selectedSkills: Skill[] = [];
  savingSkills = false;

  /** Editable working copies for rate/location/date fields in the Details sidebar. */
  editRateTarget: number | null = null;
  editHours: number | null = null;
  // The stored wording when it is not a plain number ("32 - 40 uur"). A number
  // input cannot render it, so it is shown beside the field: without it the
  // input looks empty on a row that does hold a value.
  rawHours: string | null = null;
  editLocation: string | null = null;
  editDeadline: Date | null = null;
  editStart: Date | null = null;
  editEnd: Date | null = null;
  editTitle: string | null = null;
  editDescription: string | null = null;

  saving = false;
  savingActivity = false;
  savingRates = false;
  savingContact = false;
  savingGeneral = false;
  savingNewContact = false;
  showNewContactForm = false;
  newContact: Partial<Contact> = {};
  private oppId = 0;
  errorMessage: string | null = null;

  readonly selectableStatuses = OPPORTUNITY_SELECTABLE_STATUSES;
  readonly Globals = Globals;

  constructor(@Optional() private dialogRef: NbDialogRef<any>,
              private route: ActivatedRoute,
              private router: Router,
              private dialogService: NbDialogService,
              private auth: AuthService,
              private opportunityService: OpportunityService,
              private submissionService: SubmissionService,
              private usersService: UsersService,
              private organizationCrm: OrganizationCrmService,
              private clientService: ClientService,
              private directoryService: DirectoryService,
              private notifier: NotifierService,
              private translate: TranslateService,
              private location: Location,
              private quoteService: QuoteService) {
    super();
  }

  get canCreate(): boolean {
    return (this.auth.isAdmin || this.auth.isStaffer) && !!this.auth.user.getValue()?.crm_enabled;
  }

  /** Quotes (#1697): Quotes on for the company, and Admin or the Quotes grant. */
  get canQuote(): boolean {
    const user = this.auth.user.getValue();
    return !!user?.quotes_enabled && !!user?.can_quote;
  }

  /** A new quote, pre-filled by the server from this opportunity; opens in the editor. */
  async newQuote(): Promise<void> {
    if (this.creatingQuote) { return; }
    this.creatingQuote = true;
    const quote = await this.quoteService.create(this.oppId).catch(() => null);
    this.creatingQuote = false;
    if (quote) { this.openQuote(quote.id); }
  }

  openQuote(id: number): void {
    this.dialogRef?.close();
    void this.router.navigate(['/quotes', id]);
  }

  async downloadQuote(id: number): Promise<void> {
    await this.quoteService.downloadPdf(id).catch(e => e?.reason && this.notifier.notify(e.reason, null, 'danger'));
  }

  async deleteQuote(q: QuoteListItem): Promise<void> {
    if (!await this.notifier.confirm('quotes.delete_confirm', q.number, 'quotes.delete', 'danger', 'cancel')) { return; }
    if (await this.quoteService.remove(q.id).then(() => true).catch(() => false)) {
      this.quotes = this.quotes.filter(x => x.id !== q.id);
    }
  }

  // Non-fatal: a failed (already toasted) list leaves the section empty.
  private async loadQuotes(id: number): Promise<QuoteListItem[]> {
    return this.canQuote ? this.quoteService.list(id).catch(() => []) : [];
  }

  /** Open the add-submission dialog; on success reload the submissions. */
  async openAddSubmission(): Promise<void> {
    if (!this.oppId) { return; }
    const existing = this.submissions.map(s => s.consultant_User_id);
    const created = await this.dialogService
      .open<AddSubmissionDialogComponent>(AddSubmissionDialogComponent, {
        context: {
          oppId: this.oppId,
          existingConsultantIds: existing,
          vacancyId: this.opportunity?.MarketVacancy_id ?? null,
        },
      })
      .onClose.toPromise();
    if (created) {
      await this.load(this.oppId);
    }
  }

  openSubmission(s: Submission): void {
    const componentContext: any = { id: s.id, embedded: true };
    const shareUrl = `/submissions/${s.id}`;
    const backUrl = this.location.path();
    this.location.go(shareUrl);

    const dialogRef = this.dialogService.open<PopupDialogComponent<SubmissionDetailComponent>>(PopupDialogComponent, {
      closeOnBackdropClick: false,
      dialogClass: 'wider-popup',
      context: {
        comp: SubmissionDetailComponent,
        componentContext
      }
    });

    const locSub = this.location.subscribe(() => dialogRef.close());
    this.subs.push(dialogRef.onClose.subscribe(result => {
      locSub.unsubscribe();
      if (this.location.path() === shareUrl) {
        this.location.go(backUrl);
      }
      // If deleted or changed, reload
      void this.load(this.oppId);
    }));
  }

  ngOnInit(): void {
    super.ngOnInit();
    const id = this.id || Number(this.route.snapshot.paramMap.get('id'));
    if (!id) {
      this.errorMessage = this.translate.instant('crm.err.invalid_opportunity');
      return;
    }
    this.oppId = id;
    void this.load(id);
  }

  consultantName(s: Submission): string {
    if (!s.consultant) { return `#${s.consultant_User_id}`; }
    return `${s.consultant.name_first} ${s.consultant.name_last}`.trim();
  }

  async addActivity(e: { type: string; body: string; Contact_id?: number | null; occurred_at?: string }): Promise<void> {
    this.savingActivity = true;
    try {
      const res = await this.opportunityService.logActivity(this.opportunity!.id, e.type, e.body, e.Contact_id, e.occurred_at);
      this.activities.unshift(res.data);
    } catch (err) {
      console.error('log activity failed', err);
    } finally {
      this.savingActivity = false;
    }
  }

  async editActivity(e: { id: number; body: string; occurred_at?: string }): Promise<void> {
    try {
      const updated = await this.organizationCrm.updateActivity(e.id, { body: e.body, occurred_at: e.occurred_at });
      if (!updated) { return; }
      const idx = this.activities.findIndex(a => a.id === e.id);
      if (idx !== -1) { this.activities[idx] = { ...this.activities[idx], body: updated.body, occurred_at: updated.occurred_at }; }
    } catch (err) {
      console.error('edit activity failed', err);
    }
  }

  async deleteActivity(id: number): Promise<void> {
    const deleted = await this.organizationCrm.deleteActivity(id).catch(() => false);
    if (deleted) { this.activities = this.activities.filter(a => a.id !== id); }
  }

  /**
   * Longest week the server will read back, mirroring
   * config('crm.health.max_hours_per_week'). Above it OpportunityHours refuses
   * the value, so writing one stores a number nothing can use.
   */
  private static readonly HOURS_MAX = 60;

  /** Same bound, for the input's own max attribute. */
  readonly hoursMax = OpportunityDetailComponent.HOURS_MAX;

  /**
   * The stored hours as a number, or null when the column holds anything else:
   * a window, a unit, a note. Accepts either decimal mark, both of which occur
   * in scraped values.
   */
  private plainHours(stored: string): number | null {
    return /^\d+(?:[.,]\d+)?$/.test(stored) ? Number(stored.replace(',', '.')) : null;
  }

  /** Save rate/location/date fields. Only sends fields that changed from the loaded opportunity. */
  async saveValueRate(): Promise<void> {
    if (!this.opportunity) { return; }
    const toNum = (v: number | null | undefined): number | null =>
      v != null ? Number(v) : null;

    const loadedRateTarget = toNum(this.opportunity.rate_target);
    const loadedLocation = this.opportunity.location ?? null;
    // The nbDatepicker binds a Date; normalize both sides to ISO for the diff
    // and skip an unparseable typed value instead of PUTting "Invalid date".
    // Route the stored side through fromDateString too: the API serialises a
    // date cast as midnight UTC, and formatting that directly lands on the
    // previous day west of UTC, so an untouched date would look changed and be
    // resent on every save. fromDateString drops the zone; a Date passes through.
    const iso = (v: Date | string | null | undefined): string | null =>
      v ? Globals.toISODate(Globals.fromDateString(v)) : null;

    const loadedDeadline = iso(this.opportunity.submission_deadline);
    const loadedStart = iso(this.opportunity.start_date);
    const loadedEnd = iso(this.opportunity.end_date);
    const newDeadline = iso(this.editDeadline);
    const newStart = iso(this.editStart);
    const newEnd = iso(this.editEnd);

    // The server rejects a reversed or equal pair (OpportunityDuration needs
    // end > start). Mirror it here so the user gets the actual reason instead
    // of the generic save-failed toast, and so neither date is PUT alone.
    //
    // Only when a date actually changed, mirroring the server's own gate: a row
    // written under the old rule can already hold a reversed pair, and blocking
    // unconditionally would swallow the user's unrelated rate or location edit
    // with a message about dates they never touched.
    const datesChanged = newStart !== loadedStart || newEnd !== loadedEnd;
    if (datesChanged && newStart && newEnd
        && newStart !== 'Invalid date' && newEnd !== 'Invalid date'
        && newEnd <= newStart) {
      void this.notifier.notify(this.translate.instant('crm.end_before_start'), null, 'danger');
      return;
    }

    const payload: Partial<Opportunity> = {};
    if (this.editRateTarget !== loadedRateTarget) { payload.rate_target = this.editRateTarget; }
    if (this.editLocation !== loadedLocation) { payload.location = this.editLocation; }
    // Hours are replaceable, not clearable, the same trade the dates make. An
    // empty input is the resting state of every row whose stored value is not a
    // plain number, so treating empty as a change would wipe that wording on
    // the next unrelated edit. Only a typed number is ever sent.
    //
    // Compare as numbers: a stored "36,5" and a typed 36.5 are the same week,
    // and comparing the strings would rewrite the row on an unrelated save.
    const loadedHours = this.plainHours((this.opportunity.hours_per_week ?? '').trim());
    const hoursChanged = this.editHours != null && this.editHours !== loadedHours;

    // min/max on the input are advisory: the browser does not clamp a typed
    // value and the model is written whatever the validator says, so the range
    // has to be enforced here or not at all. A refused figure would still save,
    // still report saved, and still read back as an ordinary number - the row
    // would simply stop being valued, visible only in crm:pipeline-health.
    if (hoursChanged && (this.editHours! <= 0 || this.editHours! > OpportunityDetailComponent.HOURS_MAX)) {
      void this.notifier.notify(
        this.translate.instant('crm.hours_out_of_range', { max: OpportunityDetailComponent.HOURS_MAX }),
        null, 'danger');
      return;
    }
    if (hoursChanged) {
      payload.hours_per_week = String(this.editHours);
    }
    if (newDeadline !== 'Invalid date' && newDeadline !== loadedDeadline) { payload.submission_deadline = newDeadline; }
    if (newStart !== 'Invalid date' && newStart !== loadedStart) { payload.start_date = newStart; }
    if (newEnd !== 'Invalid date' && newEnd !== loadedEnd) { payload.end_date = newEnd; }

    if (Object.keys(payload).length === 0) { return; }

    this.savingRates = true;
    try {
      const updated = await this.opportunityService.update(this.opportunity.id, payload);
      if (updated) {
        Object.assign(this.opportunity, updated);
      }
      // The typed number has replaced the stored wording; keeping the hint would
      // show a value that is no longer there.
      if (payload.hours_per_week != null) { this.rawHours = null; }
      void this.notifier.notify(this.translate.instant('crm.toast.saved'), null, 'success');
    } catch (e) {
      console.error('opportunity rate/location/date update failed', e);
      void this.notifier.notify(this.translate.instant('crm.err.save_failed'), null, 'danger');
    } finally {
      this.savingRates = false;
    }
  }

  async saveGeneral(): Promise<void> {
    if (!this.opportunity) { return; }

    const payload: Partial<Opportunity> = {};
    if (this.editTitle !== this.opportunity.title) { payload.title = this.editTitle ?? ''; }
    if (this.editDescription !== (this.opportunity.description ?? null)) { payload.description = this.editDescription ?? ''; }

    if (Object.keys(payload).length === 0) { return; }

    this.savingGeneral = true;
    try {
      const updated = await this.opportunityService.update(this.opportunity.id, payload);
      if (updated) {
        Object.assign(this.opportunity, updated);
      }
      void this.notifier.notify(this.translate.instant('crm.toast.saved'), null, 'success');
    } catch (e) {
      console.error('opportunity general update failed', e);
      void this.notifier.notify(this.translate.instant('crm.err.save_failed'), null, 'danger');
    } finally {
      this.savingGeneral = false;
    }
  }

  back(): void {
    if (this.embedded && this.dialogRef) {
      this.dialogRef.close();
    } else {
      void this.router.navigate(['opportunities', 'view', 'open']);
    }
  }

  /**
   * Dismiss the dialog when a link inside it navigates the page behind it.
   * The routerLink still performs the navigation; without this the destination
   * loads underneath and the dialog stays up, hiding it.
   *
   * A no-op on the standalone route, where there is no dialog to close.
   */
  closeIfEmbedded(event?: MouseEvent): void {
    // RouterLink hands a modified click (new tab, middle button) to the
    // browser and leaves this page in place, so the popup stays put too.
    if (event && (event.button !== 0 || event.ctrlKey || event.shiftKey || event.altKey || event.metaKey)) {
      return;
    }
    if (this.embedded && this.dialogRef) {
      // The marker lets the host skip a list reload: the page behind is being
      // navigated away from, and a paged REFRESH would re-navigate to the list
      // from the destroyed route once its fetch resolves.
      this.dialogRef.close({ navigated: true });
    }
  }

  get canDelete(): boolean {
    return this.submissions.length === 0 && !this.opportunity?.converted_Project_id;
  }

  async reopen(): Promise<void> {
    if (!confirm(this.translate.instant('crm.reopen_confirm'))) { return; }
    await this.opportunityService.reopen(this.oppId);
    await this.load(this.oppId);
  }

  async archive(): Promise<void> {
    if (!confirm(this.translate.instant('crm.archive_confirm'))) { return; }
    await this.opportunityService.setStatus(this.oppId, 'archived');
    await this.load(this.oppId);
  }

  async unarchive(): Promise<void> {
    await this.opportunityService.setStatus(this.oppId, 'open');
    await this.load(this.oppId);
  }

  async deleteOpp(): Promise<void> {
    if (!confirm(this.translate.instant('crm.delete_confirm'))) { return; }
    const res: any = await this.opportunityService.deleteOpportunity(this.oppId);
    if (res && res.deleted) {
      if (this.embedded && this.dialogRef) {
        this.dialogRef.close({ deleted: true, oppId: this.oppId });
      } else {
        void this.router.navigate(['/opportunities', 'view', 'open']);
      }
    }
  }

  /** Returns the display contact: serialized relation from API, or the picked item from the loaded list. */
  get displayContact(): { id: number; name: string; email?: string; role?: string; function?: string; phone?: string } | null {
    if (this.opportunity?.primary_contact) { return this.opportunity.primary_contact; }
    if (this.selectedContactId) {
      return this.contacts.find(c => c.id === this.selectedContactId) ?? null;
    }
    return null;
  }

  /**
   * True when the opportunity's current end client is one of the loaded
   * candidates. False for an assigned client that's since gone ARCHIVED
   * (excluded from orgCandidates) — the picker falls back to read-only
   * rather than rendering a select with nothing matching selectedOrgId.
   */
  get currentOrgKnown(): boolean {
    return this.orgCandidates.some(c => c.id === this.selectedOrgId);
  }

  /** Reassign the owner (verantwoordelijke); mirrors changeContact's optimistic pattern. */
  async changeOwner(ownerId: number | null): Promise<void> {
    if (!this.opportunity || !ownerId) { return; }
    const prev = this.opportunity.owner_User_id ?? this.opportunity.owner?.id ?? null;
    if (ownerId === prev) { return; }
    this.savingOwner = true;
    try {
      const updated = await this.opportunityService.update(this.opportunity.id, { owner_User_id: ownerId });
      if (updated) {
        Object.assign(this.opportunity, updated);
      }
      this.selectedOwnerId = ownerId;
      void this.notifier.notify(this.translate.instant('crm.toast.saved'), null, 'success');
    } catch (e) {
      console.error('opportunity owner update failed', e);
      this.selectedOwnerId = prev;
      void this.notifier.notify(this.translate.instant('crm.err.save_failed'), null, 'danger');
    } finally {
      this.savingOwner = false;
    }
  }

  /** Reassign the end client; mirrors changeOwner's optimistic pattern. */
  async changeOrg(clientId: number | null): Promise<void> {
    if (!this.opportunity || !clientId) { return; }
    const prev = this.opportunity.for_Client_id ?? null;
    if (clientId === prev) { return; }
    this.savingOrg = true;
    try {
      const updated = await this.opportunityService.update(this.opportunity.id, { for_Client_id: clientId });
      if (updated) {
        Object.assign(this.opportunity, updated);
      }
      this.selectedOrgId = clientId;
      void this.notifier.notify(this.translate.instant('crm.toast.saved'), null, 'success');
    } catch (e) {
      console.error('opportunity org update failed', e);
      this.selectedOrgId = prev;
      void this.notifier.notify(this.translate.instant('crm.err.save_failed'), null, 'danger');
    } finally {
      this.savingOrg = false;
    }
  }

  /** Reassign the role; mirrors changeOrg's optimistic pattern. Unlike owner/org, null is a valid, savable value. */
  async changeRole(roleId: number | null): Promise<void> {
    if (!this.opportunity) { return; }
    const prev = this.opportunity.ProjectRole_id ?? null;
    this.savingRole = true;
    try {
      const updated = await this.opportunityService.update(this.opportunity.id, { ProjectRole_id: roleId });
      if (updated) {
        Object.assign(this.opportunity, updated);
      }
      this.selectedRoleId = roleId;
      void this.notifier.notify(this.translate.instant('crm.toast.saved'), null, 'success');
    } catch (e) {
      console.error('opportunity role update failed', e);
      this.selectedRoleId = prev;
      void this.notifier.notify(this.translate.instant('crm.err.save_failed'), null, 'danger');
    } finally {
      this.savingRole = false;
    }
  }

  /**
   * Reassign skills; mirrors changeOrg's optimistic pattern. The list-adder
   * control holds Skill objects but the API takes ids — sending `skills`
   * instead of `skill_ids` fails silently, the backend just ignores the key.
   */
  async changeSkills(skills: Skill[]): Promise<void> {
    if (!this.opportunity) { return; }
    const prev = this.opportunity.skills ?? [];
    this.savingSkills = true;
    try {
      const payload: Partial<Opportunity> & {skill_ids?: number[]} = { skill_ids: (skills || []).map((s: any) => s.id) };
      const updated = await this.opportunityService.update(this.opportunity.id, payload as Partial<Opportunity>);
      if (updated) {
        Object.assign(this.opportunity, updated);
      }
      this.selectedSkills = skills;
      void this.notifier.notify(this.translate.instant('crm.toast.saved'), null, 'success');
    } catch (e) {
      console.error('opportunity skills update failed', e);
      this.selectedSkills = prev;
      void this.notifier.notify(this.translate.instant('crm.err.save_failed'), null, 'danger');
    } finally {
      this.savingSkills = false;
    }
  }

  async changeContact(contactId: number | null): Promise<void> {
    if (!this.opportunity) { return; }
    const prev = this.opportunity.primary_Contact_id ?? null;
    this.savingContact = true;
    try {
      const updated = await this.opportunityService.update(this.opportunity.id, { primary_Contact_id: contactId });
      if (updated) {
        Object.assign(this.opportunity, updated);
      }
      this.selectedContactId = contactId;
      void this.notifier.notify(this.translate.instant('crm.toast.saved'), null, 'success');
    } catch (e) {
      console.error('opportunity primary contact update failed', e);
      this.selectedContactId = prev;
      void this.notifier.notify(this.translate.instant('crm.err.save_failed'), null, 'danger');
    } finally {
      this.savingContact = false;
    }
  }

  toggleNewContactForm(): void {
    this.showNewContactForm = !this.showNewContactForm;
    this.newContact = {};
  }

  /** Create a contact for this opportunity's organization and set it as the primary contact. */
  async addContact(): Promise<void> {
    if (!this.opportunity || !this.newContact.name?.trim()) { return; }
    this.savingNewContact = true;
    try {
      const created = await this.organizationCrm.createContact(this.opportunity.for_Client_id, this.newContact);
      if (created) {
        this.contacts = [...this.contacts, created];
        this.showNewContactForm = false;
        this.newContact = {};
        await this.changeContact(created.id);
      }
    } catch (e) {
      console.error('create contact failed', e);
    } finally {
      this.savingNewContact = false;
    }
  }

  async changeStatus(value: OpportunityStatus): Promise<void> {
    if (!this.opportunity) { return; }
    this.saving = true;
    this.errorMessage = null;
    try {
      await this.opportunityService.setStatus(this.opportunity.id, value);
      void this.notifier.notify(this.translate.instant('crm.toast.status_updated'), null, 'success');
      await this.load(this.oppId);
    } catch (e) {
      console.error('opportunity status update failed', e);
      this.errorMessage = this.translate.instant('crm.err.status_failed');
      // Revert the working copy so the dropdown doesn't keep showing the failed value.
      this.selectedStatus = this.opportunity.status;
    } finally {
      this.saving = false;
    }
  }

  /** Owner candidates (staffers/admins) are non-fatal: on failure the owner row degrades to a read-only name. */
  private async loadOwnerCandidates(): Promise<UserRef[]> {
    try {
      return await this.usersService.lookupUsers([2, 3]);
    } catch (e) {
      console.error('owner candidates load failed', e);
      return [];
    }
  }

  /**
   * End-client candidates: any already-known Client of this tenant, non-fatal
   * on failure (degrades to a read-only org row). ARCHIVED clients are
   * excluded — an archived org shouldn't be pickable.
   */
  private async loadOrgCandidates(): Promise<ClientRef[]> {
    try {
      return await this.clientService.loadAllProspects();
    } catch (e) {
      console.error('org candidates load failed', e);
      return [];
    }
  }

  /**
   * Role candidates: non-fatal on failure. Often empty for a tenant, since the
   * ProjectRole directory ships unseeded — an empty list still renders a
   * working picker, it just has nothing beyond "none" to offer.
   */
  private async loadRoleOptions(): Promise<ProjectRole[]> {
    try {
      return await this.directoryService.getProjectRoles();
    } catch (e) {
      console.error('role options load failed', e);
      return [];
    }
  }

  private async load(id: number): Promise<void> {
    this.loading.next(true);
    this.errorMessage = null;
    try {
      // opp/submissions/activities/owner-candidates/org-candidates don't depend
      // on each other, so they load concurrently instead of one round trip
      // after another — only contacts needs opp (for the client ids) and
      // waits for it below.
      const [opp, subs, acts, ownerCandidates, orgCandidates, roleOptions, quotes] = await Promise.all([
        this.opportunityService.show(id),
        this.submissionService.listForOpportunity(id),
        // Activities are non-fatal: the timeline is secondary to the deal, so a
        // failed (already toasted) load degrades to the empty state.
        this.opportunityService.activities(id).catch((e) => {
          console.error('activities load failed', e);
          return null;
        }),
        this.loadOwnerCandidates(),
        this.loadOrgCandidates(),
        this.loadRoleOptions(),
        this.loadQuotes(id),
      ]);
      if (!opp) {
        this.errorMessage = this.translate.instant('crm.err.load_opportunity');
        return;
      }
      this.opportunity = opp;
      this.selectedStatus = opp.status;
      this.selectedContactId = opp.primary_Contact_id ?? null;
      // Coerce decimal strings returned by the API (e.g. "85.00") to numbers.
      // Use null when the value is absent so inputs show blank.
      const toEditNum = (v: number | null | undefined): number | null =>
        v != null ? Number(v) : null;
      this.editRateTarget = toEditNum(opp.rate_target);
      // Only a plain number reaches the number input. Anything else (a stated
      // window, a unit, a note) stays in rawHours untouched, so that wording is
      // never rewritten as a side effect of opening the form.
      const storedHours = (opp.hours_per_week ?? '').trim();
      this.editHours = this.plainHours(storedHours);
      this.rawHours = storedHours !== '' && this.editHours === null ? storedHours : null;
      this.editLocation = opp.location ?? null;
      this.editDeadline = opp.submission_deadline ? Globals.fromDateString(opp.submission_deadline) : null;
      this.editStart = opp.start_date ? Globals.fromDateString(opp.start_date) : null;
      this.editEnd = opp.end_date ? Globals.fromDateString(opp.end_date) : null;
      this.editTitle = opp.title;
      this.editDescription = opp.description ?? null;

      // Owner candidates are already resolved and contacts don't depend on
      // submissions either, so assign/fetch both before the submissions guard.
      this.ownerCandidates = ownerCandidates;
      this.selectedOwnerId = opp.owner_User_id ?? opp.owner?.id ?? null;
      this.orgCandidates = orgCandidates;
      this.selectedOrgId = opp.for_Client_id ?? null;
      this.roleOptions = roleOptions;
      this.selectedRoleId = opp.ProjectRole_id ?? null;
      this.selectedSkills = opp.skills ?? [];
      this.quotes = quotes;

      // Contacts are non-fatal: if the endpoint is unreachable, the picker
      // degrades gracefully to the "no contacts" hint. Both counterparties are
      // offered, since the person on a brokered deal is often at the broker.
      this.contacts = await this.organizationCrm.listDealContacts(
        {id: opp.for_Client_id, name: opp.end_client?.name},
        {id: opp.via_Client_id, name: opp.broker?.name},
      );

      if (!subs) {
        this.errorMessage = this.translate.instant('crm.board.load_error');
        return;
      }
      this.submissions = subs.data ?? [];
      this.activities = acts?.data ?? [];
    } catch (e) {
      console.error('opportunity detail load failed', e);
      this.errorMessage = this.translate.instant('crm.err.load_opportunity');
    } finally {
      this.loading.next(false);
    }
  }
}
